import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AcademicCapIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  PencilAltIcon,
  UserIcon,
  XCircleIcon,
} from "@heroicons/react/solid";
import UsersList from "../components/UsersList";
import ModernDatePicker from "../components/ModernDatePicker";
import {
  FiMapPin,
  FiMonitor,
  FiAlertTriangle,
  FiPlus,
  FiSave,
  FiSend,
  FiTrash2,
} from "react-icons/fi";
import { apiFetch } from "../utils/apiClient";
import { showError } from "../components/ErrorPopup";
import { useLocation, useNavigate } from "react-router-dom";
import DraftsPopup from "../components/DraftsPopup";
import {
  CANADIAN_PROVINCES,
  COUNTRIES,
} from "../utils/formationLocationOptions";

const inputClassName =
  "mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-3 text-[15px] text-slate-800 shadow-sm outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const labelClassName = "block text-sm font-medium text-slate-700";

const sectionCardClassName =
  "mt-5 w-full rounded-xl border border-gray-200 bg-white shadow-sm [&>div>*]:max-w-5xl";

const parseDateValueUtc = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? date
    : null;
};

const calculateInclusiveDays = (startValue, endValue) => {
  const start = parseDateValueUtc(startValue);
  const end = parseDateValueUtc(endValue);
  if (!start || !end || end < start) return "";
  return Math.floor((end - start) / 86400000) + 1;
};

const addDaysToDateValue = (value, numberOfDays) => {
  const date = parseDateValueUtc(value);
  const days = Number(numberOfDays);
  if (!date || !Number.isFinite(days) || days < 1) return "";
  date.setUTCDate(date.getUTCDate() + days - 1);
  return date.toISOString().slice(0, 10);
};

const deriveVoyageExterieurOntario = ({
  modeParticipation,
  pays,
  province,
}) => {
  if (modeParticipation === "virtuel") return "non";
  if (modeParticipation !== "presentiel" || !pays) return "";
  if (pays !== "Canada") return "oui";
  if (!province) return "";
  return province === "Ontario" ? "non" : "oui";
};

const normalizeLoadedLocationAndDates = (data) => {
  const legacyLocation = String(data?.provincePays || "").trim();
  const legacyProvince = CANADIAN_PROVINCES.find(
    (province) => province.toLowerCase() === legacyLocation.toLowerCase(),
  );
  const legacyCountry = COUNTRIES.find(
    (country) => country.toLowerCase() === legacyLocation.toLowerCase(),
  );
  const pays = data?.pays || (legacyProvince ? "Canada" : legacyCountry || legacyLocation || "Canada");
  const province =
    pays === "Canada" ? data?.province || legacyProvince || "" : "";
  const modeParticipation =
    data?.modeParticipation ||
    (data?.ville || data?.pays || data?.province || legacyLocation
      ? "presentiel"
      : "");
  const dateFin =
    data?.dateFin || addDaysToDateValue(data?.dateDebut, data?.nombreJours);

  return {
    modeParticipation,
    ville: modeParticipation === "virtuel" ? "" : data?.ville || "",
    pays: modeParticipation === "virtuel" ? "" : pays,
    province: modeParticipation === "virtuel" ? "" : province,
    voyageExterieurOntario: deriveVoyageExterieurOntario({
      modeParticipation,
      pays: modeParticipation === "virtuel" ? "" : pays,
      province: modeParticipation === "virtuel" ? "" : province,
    }),
    dateFin,
    nombreJours: calculateInclusiveDays(data?.dateDebut, dateFin),
  };
};

function InlineError({ message }) {
  if (!message) return null;

  return (
    <p className="mt-1 flex items-center gap-1.5 text-sm text-red-500" role="alert">
      <span className="inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-red-100 text-[10px] font-bold text-red-500">
        !
      </span>
      {message}
    </p>
  );
}

const COST_OPTIONS = [
  { value: "inscription", label: "Frais d’inscription" },
  { value: "transport", label: "Transport" },
  { value: "hebergement", label: "Hébergement" },
  { value: "repas", label: "Repas" },
  { value: "suppleance", label: "Suppléance (235 $ par jour)" },
  { value: "autre", label: "Autre dépense" },
];

const PARTICIPATION_MODE_OPTIONS = [
  { value: "presentiel", label: "En présentiel", icon: FiMapPin },
  { value: "virtuel", label: "En ligne", icon: FiMonitor },
];

const inferExpenseRows = (data) => {
  if (Array.isArray(data?.depensesPrevues)) {
    return data.depensesPrevues.map((depense) => ({
      type: depense?.type || "",
      montant:
        depense?.montant === undefined || depense?.montant === null
          ? ""
          : String(depense.montant),
      details: depense?.details || "",
    }));
  }

  // Conversion des brouillons créés pendant le développement de l'ancienne structure.
  const legacyFields = [
    ["inscription", "coutInscription", "detailsInscription"],
    ["transport", "coutTransport", "detailsTransport"],
    ["hebergement", "coutHebergement", "detailsHebergement"],
    ["repas", "coutRepas", "detailsRepas"],
    ["suppleance", "coutSuppleance", "detailsSuppleance"],
    ["autre", "coutAutre", "detailsAutre"],
  ];

  return legacyFields
    .filter(([, amountField]) => data?.[amountField] != null)
    .map(([type, amountField, detailsField]) => ({
      type,
      montant: String(data[amountField]),
      details: data?.[detailsField] || "",
    }));
};

const generateNumeroFormulaire = () => {
  const date = new Date();
  const yy = date.getFullYear().toString().slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const randomNumber = Math.floor(1000 + Math.random() * 9000);

  return `CSV-${yy}${mm}${dd}${randomNumber}-PF`;
};

const formatParticipationDraftDate = (draft) => {
  const valeur = draft.dateDebut || draft.dateSoumission;
  if (!valeur) return "";
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return "";
  const dateLabel = new Intl.DateTimeFormat("fr-CA", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
  return `${draft.dateDebut ? "Formation" : "Créé le"} : ${dateLabel}`;
};

function SectionHeader({ icon: Icon, iconColor = "text-blue-400", children }) {
  return (
    <div className="mt-8 w-full rounded-t-xl bg-gradient-to-r from-[#182a43] to-[#40516a] py-3 shadow-sm">
      <p className="ml-4 flex items-center text-base font-semibold tracking-wide text-white">
        <Icon className={`h-4 w-4 ${iconColor} mr-2`} aria-hidden="true" />
        {children}
      </p>
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  min,
  step,
  required = false,
  disabled = false,
  readOnly = false,
  error = "",
}) {
  return (
    <div className="w-full min-w-0">
      <label htmlFor={name} className={labelClassName}>
        {label}
        {required && <span className="ml-1 text-red-500"> *</span>} :
      </label>
      <input
        id={name}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        min={min}
        step={step}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        className={`${inputClassName} ${
          disabled || readOnly ? "bg-slate-50 text-slate-700" : "bg-white"
        } ${error ? "border-red-400 focus:border-red-500 focus:ring-red-100" : ""}`}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      <div id={`${name}-error`}>
        <InlineError message={error} />
      </div>
    </div>
  );
}

function SelectField({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = "Sélectionner",
  required = false,
  error = "",
}) {
  const normalizedOptions = options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  );
  const hasLegacyValue =
    value && !normalizedOptions.some((option) => option.value === value);

  return (
    <div className="w-full min-w-0">
      <label htmlFor={name} className={labelClassName}>
        {label}
        {required && <span className="ml-1 text-red-500"> *</span>} :
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className={`${inputClassName} ${
          error ? "border-red-400 focus:border-red-500 focus:ring-red-100" : ""
        }`}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${name}-error` : undefined}
      >
        <option value="">{placeholder}</option>
        {hasLegacyValue && <option value={value}>{value}</option>}
        {normalizedOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div id={`${name}-error`}>
        <InlineError message={error} />
      </div>
    </div>
  );
}

function ReadOnlyInfo({ label, value }) {
  return (
    <div className="min-w-0 py-2">
      <p className="text-[13px] font-semibold uppercase tracking-wide text-slate-600">
        {label}
      </p>
      <p
        className="mt-1 break-words text-[15px] font-medium text-slate-900"
        title={value}
      >
        {value || "—"}
      </p>
    </div>
  );
}

function YesNoField({
  label,
  name,
  value,
  onChange,
  required = false,
  error = "",
}) {
  return (
    <div className="w-full min-w-0">
      <div className="grid grid-row-2 items-center gap-4">
        <label className={labelClassName}>
          {label}
          {required && <span className="ml-1 text-red-500"> *</span>} :
        </label>
        <div
          className="flex flex-wrap items-center gap-3"
          role="radiogroup"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${name}-error` : undefined}
        >
          {["oui", "non"].map((option) => {
            const isSelected = value === option;

            return (
              <label
                key={option}
                className={`flex min-w-24 items-center justify-center rounded-lg border px-6 py-2.5 text-sm font-medium cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-300 focus-within:ring-offset-1 ${
                  isSelected
                    ? "bg-blue-100 text-blue-600 border-blue-300"
                    : "bg-white text-gray-500 border-gray-300 hover:border-gray-400 hover:text-gray-600"
                }`}
              >
                <input
                  type="radio"
                  name={name}
                  value={option}
                  checked={isSelected}
                  onChange={onChange}
                  className="sr-only"
                  required={required}
                />
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </label>
            );
          })}
        </div>
      </div>
      <div id={`${name}-error`}>
        <InlineError message={error} />
      </div>
    </div>
  );
}

function MoneyField({
  label,
  name,
  value,
  onChange,
  required = false,
  error = "",
  hideLabelOnDesktop = false,
}) {
  const handleAmountChange = (event) => {
    const normalizedValue = event.target.value
      .replace(",", ".")
      .replace(/[^0-9.]/g, "");
    const [integerPart, ...decimalParts] = normalizedValue.split(".");
    const amount =
      decimalParts.length > 0
        ? `${integerPart}.${decimalParts.join("")}`
        : integerPart;

    onChange({ target: { name, value: amount } });
  };

  return (
    <div className="w-full min-w-0">
      <label
        htmlFor={name}
        className={`block text-sm font-medium leading-5 text-slate-700 ${
          hideLabelOnDesktop ? "lg:sr-only" : ""
        }`}
      >
        {label}
        {required && <span className="text-red-700"> *</span>} :
      </label>
      <div className="relative mt-1.5 w-full">
        <input
          id={name}
          type="text"
          name={name}
          value={value}
          onChange={handleAmountChange}
          inputMode="decimal"
          required={required}
          pattern={required ? "^(?=.*[1-9])\\d*(?:\\.\\d+)?$" : undefined}
          title={required ? "Le montant doit être supérieur à zéro." : undefined}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${name}-error` : undefined}
          className={`h-10 w-full rounded-lg border bg-white py-2 pl-3 pr-9 text-right text-[15px] text-slate-800 shadow-sm outline-none transition-all duration-200 focus:ring-2 ${
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-100"
              : "border-slate-300 focus:border-blue-500 focus:ring-blue-100"
          }`}
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
          $
        </span>
      </div>
      <div id={`${name}-error`}>
        <InlineError message={error} />
      </div>
    </div>
  );
}

function ParticipationFormation({ user, enabled, checkGroupAdmin }) {
  const [selectedForm, setSelectedForm] = useState(null);
  const [formId, setFormId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [message, setMessage] = useState("");
  const [couleur, setCouleur] = useState("");
  const [errors, setErrors] = useState({});
  const [commentaire, setCommentaire] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [showDraftPopup, setShowDraftPopup] = useState(false);
  const [costRows, setCostRows] = useState([]);
  const nextCostRowId = useRef(0);
  const formRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const numeroFormulaire = params.get("numeroFormulaire");
  const page = params.get("page");

  useEffect(() => {
    if (!enabled || !user?.id || numeroFormulaire || page) return;

    const controller = new AbortController();
    const fetchDrafts = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/participation-formation/brouillons`,
          { signal: controller.signal },
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || data.message);
        if (controller.signal.aborted) return;

        setDrafts(data);
        setShowDraftPopup(data.length > 0);
      } catch (error) {
        if (!controller.signal.aborted) console.error(error);
      }
    };

    fetchDrafts();
    return () => controller.abort();
  }, [enabled, user?.id, numeroFormulaire, page]);

  const handleDeleteDraft = async (id) => {
    try {
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/modify-etatdemande/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            etatDemande: "Supprimé",
            nomFormulaire: "Participation à une formation",
            userId: user.id,
            nom: user.displayName,
            email: user.mail,
            commentaire: "",
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message);
      setDrafts((current) => current.filter((draft) => draft._id !== id));
    } catch (error) {
      console.error(error);
      showError(error);
    }
  };

  const [formData, setFormData] = useState({
    numeroFormulaire: generateNumeroFormulaire(),
    dateSoumission: new Date().toISOString().split("T")[0],
    userId: "",
    nom: "",
    courriel: "",
    matricule: "",
    lieuTravail: "",
    poste: "",
    managerInfo: {
      nom: "",
      email: "",
      userId: "",
      matricule: "",
      etatDemande: "",
    },
    programmeurInfo: {
      nom: "",
      email: "",
      userId: "",
      matricule: "",
      etatDemande: "",
    },
    directionSecteurSurintendance: {
      nom: "",
      email: "",
      userId: "",
      matricule: "",
    },
    descriptionObjectifs: "",
    modeParticipation: "",
    ville: "",
    pays: "Canada",
    province: "Ontario",
    provincePays: "Ontario",
    dateDebut: "",
    dateFin: "",
    nombreJours: "",
    voyageExterieurOntario: "",
    suppleanceRequise: "",
    nombreJoursSuppleance: "",
    suppleanceFondsCentraux: "",
    justificationFondsCentraux: "",
    depensesPrevues: [],
    signatureElectronique: "non",
    etatDemande: "",
    traitant: [],
  });

  useEffect(() => {
    const displayName = user?.displayName || "";

    setFormData((current) => ({
      ...current,
      userId: current.userId || user?.id || "",
      nom: current.nom || displayName,
      courriel: current.courriel || user?.mail || user?.userPrincipalName || "",
      matricule: current.matricule || user?.employeeId || "",
      lieuTravail: current.lieuTravail || user?.officeLocation || "",
      poste: current.poste || user?.jobTitle || "",
    }));
  }, [user]);

  useEffect(() => {
    if (!numeroFormulaire || !page || !user?.id) return;

    const controller = new AbortController();

    const fetchForm = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/selectedFormParticipationFormation?numeroFormulaire=${encodeURIComponent(numeroFormulaire)}&page=${encodeURIComponent(page)}`,
          { signal: controller.signal },
        );
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || result.message);
        }

        if (controller.signal.aborted) return;

        const expenseRows = inferExpenseRows(result).map((depense) => ({
          ...depense,
          id: `cost-${nextCostRowId.current++}`,
        }));
        const normalizedLocationAndDates = normalizeLoadedLocationAndDates(result);
        setSelectedForm(result);
        setFormId(result._id);
        setFormData((current) => ({
          ...current,
          ...result,
          ...normalizedLocationAndDates,
          depensesPrevues: expenseRows.map(({ type, montant, details }) => ({
            type,
            montant,
            details,
          })),
        }));
        setCostRows(expenseRows);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error(error);
          showError(error);
        }
      }
    };

    fetchForm();
    return () => controller.abort();
  }, [numeroFormulaire, page, user?.id]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => {
      const updated = { ...current, [name]: value };

      if (name === "modeParticipation") {
        if (value === "virtuel") {
          updated.ville = "";
          updated.pays = "";
          updated.province = "";
          updated.provincePays = "";
        } else if (value === "presentiel") {
          updated.pays = current.pays || "Canada";
          updated.province =
            updated.pays === "Canada" ? current.province || "Ontario" : "";
          updated.provincePays =
            updated.pays === "Canada" ? updated.province : updated.pays;
        }
      }

      if (name === "pays") {
        updated.province =
          value === "Canada" ? current.province || "Ontario" : "";
        updated.provincePays =
          value === "Canada" ? updated.province : value;
      }

      if (name === "province") {
        updated.provincePays = value;
      }

      if (["modeParticipation", "pays", "province"].includes(name)) {
        updated.voyageExterieurOntario =
          deriveVoyageExterieurOntario(updated);
      }

      if (name === "dateDebut" || name === "dateFin") {
        updated.nombreJours = calculateInclusiveDays(
          updated.dateDebut,
          updated.dateFin,
        );
      }

      if (name === "suppleanceRequise" && value === "non") {
        updated.nombreJoursSuppleance = "";
        updated.suppleanceFondsCentraux = "non";
        updated.justificationFondsCentraux = "";
      }

      if (name === "suppleanceFondsCentraux" && value === "non") {
        updated.justificationFondsCentraux = "";
      }

      return updated;
    });

    setErrors((current) => {
      if (!current[name]) {
        return current;
      }
      return {
        ...current,
        [name]: "",
        ...(name === "modeParticipation"
          ? { ville: "", pays: "", province: "" }
          : {}),
        ...(name === "pays" ? { province: "" } : {}),
        ...(name === "dateDebut" ? { dateFin: "" } : {}),
        ...(name === "dateDebut" || name === "dateFin"
          ? { nombreJours: "" }
          : {}),
      };
    });
  };

  const renderError = (field) => <InlineError message={errors[field]} />;

  const addCostRow = () => {
    setCostRows((current) => {
      if (current.some(({ type }) => !type)) {
        return current;
      }

      return [
        ...current,
        {
          id: `cost-${nextCostRowId.current++}`,
          type: "",
          montant: "",
          details: "",
        },
      ];
    });
  };

  const handleCostTypeChange = (rowId, type) => {
    setCostRows((current) =>
      current.map((row) =>
        row.id === rowId
          ? { ...row, type, montant: "", details: "" }
          : row,
      ),
    );

    setErrors((current) => {
      const updated = { ...current };
      Object.keys(updated)
        .filter((key) => key.startsWith("costDetails-"))
        .forEach((key) => delete updated[key]);
      delete updated[`costType-${rowId}`];
      delete updated[`costAmount-${rowId}`];
      delete updated[`costDetails-${rowId}`];
      delete updated.totalEstime;
      return updated;
    });
  };

  const handleCostRowChange = (rowId, field, value) => {
    setCostRows((current) =>
      current.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row,
      ),
    );
    setErrors((current) => {
      const updated = { ...current };
      delete updated[
        field === "montant"
          ? `costAmount-${rowId}`
          : `costDetails-${rowId}`
      ];
      if (field === "montant") delete updated.totalEstime;
      return updated;
    });
  };

  const removeCostRow = (rowId) => {
    setCostRows((current) => current.filter(({ id }) => id !== rowId));

    setErrors((current) => {
      const updated = { ...current };
      Object.keys(updated)
        .filter((key) => key.startsWith("costDetails-"))
        .forEach((key) => delete updated[key]);
      delete updated[`costType-${rowId}`];
      delete updated[`costAmount-${rowId}`];
      delete updated[`costDetails-${rowId}`];
      delete updated.totalEstime;
      return updated;
    });
  };

  const getSelectedCostErrors = () => {
    const nextErrors = {};
    const isPositive = (value) =>
      value !== "" && Number.isFinite(Number(value)) && Number(value) > 0;
    const typeCounts = costRows.reduce((counts, row) => {
      if (row.type) counts[row.type] = (counts[row.type] || 0) + 1;
      return counts;
    }, {});

    costRows.forEach((row) => {
      if (!row.type) {
        nextErrors[`costType-${row.id}`] =
          "Veuillez sélectionner un type de dépense.";
        return;
      }

      if (!isPositive(row.montant)) {
        nextErrors[`costAmount-${row.id}`] =
          "Le montant doit être supérieur à zéro.";
      }

      if (typeCounts[row.type] > 1 && !row.details?.trim()) {
        nextErrors[`costDetails-${row.id}`] =
          "Les détails sont obligatoires lorsque ce type de dépense est sélectionné plusieurs fois.";
      }
    });

    return nextErrors;
  };

  const validateForm = () => {
    const nextErrors = getSelectedCostErrors();
    const isPositive = (value) =>
      value !== "" && Number.isFinite(Number(value)) && Number(value) > 0;

    if (!isPositive(total)) {
      nextErrors.totalEstime =
        "Le total estimé doit être supérieur à zéro.";
    }

    if (!formData.managerInfo?.userId) {
      nextErrors.managerInfo =
        "Veuillez sélectionner la supervision immédiate.";
    }
    if (!formData.directionSecteurSurintendance?.userId) {
      nextErrors.directionSecteurSurintendance =
        "Veuillez sélectionner la direction de service / secteur / surintendance.";
    }
    if (!formData.descriptionObjectifs?.trim()) {
      nextErrors.descriptionObjectifs =
        "Veuillez inscrire une courte description et les objectifs.";
    }
    if (!["presentiel", "virtuel"].includes(formData.modeParticipation)) {
      nextErrors.modeParticipation =
        "Veuillez sélectionner le mode de participation.";
    }
    if (formData.modeParticipation === "presentiel") {
      if (!formData.ville?.trim()) {
        nextErrors.ville = "Veuillez inscrire la ville.";
      }
      if (!formData.pays?.trim()) {
        nextErrors.pays = "Veuillez sélectionner le pays.";
      }
      if (formData.pays === "Canada" && !formData.province?.trim()) {
        nextErrors.province =
          "Veuillez sélectionner la province ou le territoire.";
      }
    }
    if (!formData.dateDebut) {
      nextErrors.dateDebut = "Veuillez sélectionner la date de début.";
    }
    if (!formData.dateFin) {
      nextErrors.dateFin = "Veuillez sélectionner la date de fin.";
    } else if (
      formData.dateDebut &&
      parseDateValueUtc(formData.dateFin) &&
      parseDateValueUtc(formData.dateDebut) &&
      parseDateValueUtc(formData.dateFin) < parseDateValueUtc(formData.dateDebut)
    ) {
      nextErrors.dateFin =
        "La date de fin doit être égale ou postérieure à la date de début.";
    }
    if (!isPositive(formData.nombreJours)) {
      nextErrors.nombreJours =
        "Le nombre de jours n'a pas pu être calculé.";
    }
    if (!["oui", "non"].includes(formData.voyageExterieurOntario)) {
      nextErrors.voyageExterieurOntario = "Veuillez sélectionner Oui ou Non.";
    }
    if (!["oui", "non"].includes(formData.suppleanceRequise)) {
      nextErrors.suppleanceRequise = "Veuillez sélectionner Oui ou Non.";
    }
    if (formData.suppleanceRequise === "oui") {
      if (!isPositive(formData.nombreJoursSuppleance)) {
        nextErrors.nombreJoursSuppleance =
          "Le nombre de jours de suppléance doit être supérieur à zéro.";
      }
      if (!["oui", "non"].includes(formData.suppleanceFondsCentraux)) {
        nextErrors.suppleanceFondsCentraux =
          "Veuillez sélectionner Oui ou Non.";
      }
      if (
        formData.suppleanceFondsCentraux === "oui" &&
        !formData.justificationFondsCentraux?.trim()
      ) {
        nextErrors.justificationFondsCentraux =
          "Veuillez inscrire une justification.";
      }
    }
    if (formData.signatureElectronique !== "oui") {
      nextErrors.signatureElectronique =
        "Veuillez confirmer que les informations fournies sont exactes, complètes et vérifiées.";
    }

    setErrors(nextErrors);

    const firstInvalidField = Object.keys(nextErrors)[0];
    if (firstInvalidField) {
      window.requestAnimationFrame(() => {
        const errorElement = document.getElementById(
          `${firstInvalidField}-error`,
        );
        const fieldElement = document.getElementById(firstInvalidField);
        (errorElement || fieldElement)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleCertificationChange = (event) => {
    setFormData((current) => ({
      ...current,
      signatureElectronique: event.target.checked ? "oui" : "non",
    }));
    setErrors((current) => ({ ...current, signatureElectronique: "" }));
  };

  const handleUserSelect = (selectedUser, fieldName) => {
    setFormData((current) => ({
      ...current,
      [fieldName]: {
        nom: selectedUser.displayName,
        email: selectedUser.mail,
        userId: selectedUser.id,
        matricule: selectedUser.employeeId,
      },
    }));
    setErrors((current) => ({ ...current, [fieldName]: "" }));
  };

  const total = useMemo(() => {
    return costRows.reduce((sum, row) => {
      const amount = Number.parseFloat(row.montant);
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
  }, [costRows]);

  const formattedTotal = new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD",
  }).format(total);

  const handleFormAction = async (event, actionType, updatedTraitant = null) => {
    event.preventDefault();

    const updatedFormData = {
      ...formData,
      depensesPrevues: costRows
        .filter(({ type }) => type)
        .map(({ type, montant, details }) => ({
          type,
          montant: Number(montant),
          details: details.trim(),
        })),
      dateSoumission: new Date(),
      etatDemande: actionType,
      totalEstime: total,
      traitant: updatedTraitant ?? formData.traitant ?? [],
      action: actionType,
    };

    const method = formId ? "PUT" : "POST";
    const url = formId
      ? `${process.env.REACT_APP_API_BASE_URL}/form/${formId}?form=ParticipationFormation`
      : `${process.env.REACT_APP_API_BASE_URL}/form?form=ParticipationFormation`;

    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedFormData),
      });
      const result = await response.json();

      if (!response.ok) {
        if (result.errors) setErrors(result.errors);
        const validationMessage = result.errors
          ? Object.values(result.errors)[0]
          : null;
        throw new Error(validationMessage || result.error || result.message);
      }

      if (result.form?._id) setFormId(result.form._id);
      setErrors({});
      setFormData((current) => ({
        ...current,
        etatDemande: result.form?.etatDemande || actionType,
        traitant: result.form?.traitant || updatedFormData.traitant,
        managerInfo: result.form?.managerInfo || current.managerInfo,
        programmeurInfo:
          result.form?.programmeurInfo || current.programmeurInfo,
      }));

      const feedbackByAction = {
        Brouillon: {
          message:
            "Succès ! Le formulaire a été enregistré. Veuillez noter que l’enregistrement ne signifie pas que le formulaire a été soumis.",
          couleur: "text-gray-700",
        },
        "En traitement": {
          message: "Succès ! La demande est maintenant en traitement.",
          couleur: "text-green-600",
        },
        Approuvé: {
          message: "Succès ! Le formulaire a été approuvé.",
          couleur: "text-green-600",
        },
        Rejeté: {
          message: "Le formulaire a été rejeté.",
          couleur: "text-red-700",
        },
      };
      const feedback = result.transfertProgrammeur
        ? {
            message:
              "La demande a été approuvée par la supervision et transmise pour l’approbation finale.",
            couleur: "text-green-600",
          }
        : feedbackByAction[actionType];

      if (feedback) {
        setMessage(feedback.message);
        setCouleur(feedback.couleur);
        setIsSubmitted(true);
        await new Promise((resolve) => window.setTimeout(resolve, 3000));
        setIsSubmitted(false);
      }

      if (actionType === "Brouillon") {
        return;
      }

      if (page === "Mes taches") {
        if (window.opener && !window.opener.closed) {
          window.close();
        } else {
          navigate("/taches", { state: { selectedForm: null } });
        }
      } else {
        navigate("/demande", { state: { selectedForm: null } });
      }
    } catch (error) {
      console.error(error);
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = (event) => {
    handleFormAction(event, "Brouillon");
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    const updatedTraitant = [
      ...(formData.traitant || []),
      {
        userId: formData.managerInfo.userId,
        nom: formData.managerInfo.nom,
        email: formData.managerInfo.email,
        matricule: formData.managerInfo.matricule,
        commentaire: "",
        etatDemande: "En traitement",
        dateHeure: new Date(),
      },
    ];

    handleFormAction(event, "En traitement", updatedTraitant);
  };

  const addTraitantEntry = (etatDemande) => [
    ...(formData.traitant || []),
    {
      userId: user?.id || "",
      nom: user?.displayName || "",
      email: user?.mail || user?.userPrincipalName || "",
      matricule: user?.employeeId || "",
      commentaire,
      etatDemande,
      dateHeure: new Date(),
    },
  ];

  const handleApprouve = (event) => {
    handleFormAction(event, "Approuvé", addTraitantEntry("Approuvé"));
  };

  const handleRejeter = (event) => {
    handleFormAction(event, "Rejeté", addTraitantEntry("Rejeté"));
  };

  const lastTraitant = formData.traitant?.at(-1);
  const canProcess =
    page === "Mes taches" &&
    formData.etatDemande === "En traitement" &&
    (lastTraitant?.userId === user?.id || checkGroupAdmin);
  const formationHorsCanada =
    formData.modeParticipation === "presentiel" &&
    Boolean(formData.pays) &&
    formData.pays !== "Canada";
  const formationHorsOntario =
    formData.modeParticipation === "presentiel" &&
    ((formData.pays === "Canada" &&
      Boolean(formData.province) &&
      formData.province !== "Ontario") ||
      formationHorsCanada);
  const estEtapeSuperviseur =
    canProcess &&
    Boolean(formData.managerInfo?.userId) &&
    lastTraitant?.userId === formData.managerInfo.userId;
  const superviseurEstProgrammeur =
    (Boolean(formData.programmeurInfo?.userId) &&
      formData.programmeurInfo.userId === formData.managerInfo?.userId) ||
    (user?.id === formData.managerInfo?.userId &&
      user?.jobTitle?.trim() === "_Programmeur");
  const afficherAvertissementAvantSoumission =
    formationHorsOntario &&
    (!selectedForm || ["Brouillon", "Rejeté"].includes(formData.etatDemande));
  const afficherAvertissementSuperviseur =
    formationHorsOntario && estEtapeSuperviseur && !superviseurEstProgrammeur;
  const isReadOnly =
    page === "Mes taches" ||
    (page === "Mes demandes" &&
      Boolean(numeroFormulaire) &&
      (!selectedForm ||
        !["Brouillon", "Rejeté"].includes(formData.etatDemande)));
  // Afficher les étapes terminées même sans commentaire, mais masquer
  // l'affectation courante tant qu'elle est encore « En traitement ».
  const historiqueTraitement = (formData.traitant || []).filter(
    (traitant) => traitant.etatDemande !== "En traitement",
  );

  if (
    (!enabled && !selectedForm && page !== "Mes taches") ||
    (!enabled &&
      selectedForm &&
      selectedForm.etatDemande === "Brouillon" &&
      page !== "Mes taches")
  ) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6 text-center">
        <XCircleIcon className="h-16 w-16 text-red-500 mb-4 animate-pulse" />
        <h3 className="text-2xl font-bold text-red-600 mb-2">
          Formulaire de participation à une formation fermé
        </h3>
        <p className="text-gray-700 max-w-md">
          Le formulaire est désormais fermé.
        </p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#f7f7f8] px-4 py-6 sm:px-6 lg:px-8">
      {showDraftPopup && (
        <DraftsPopup
          drafts={drafts}
          formTitle="Participation à une formation"
          formatDate={formatParticipationDraftDate}
          onOpen={(draft) => {
            setShowDraftPopup(false);
            navigate(
              `/participationFormation?numeroFormulaire=${encodeURIComponent(draft.numeroFormulaire)}&page=Mes%20demandes`,
            );
          }}
          onDelete={handleDeleteDraft}
          onClose={() => setShowDraftPopup(false)}
        />
      )}
      <form
        ref={formRef}
        onSubmit={(event) => event.preventDefault()}
        className="mx-auto w-full max-w-[1600px]"
      >
        <fieldset disabled={isReadOnly} className="contents">
        <dl className="ml-auto grid w-full max-w-md grid-cols-1 rounded-lg border border-gray-200 bg-white text-sm sm:w-fit sm:max-w-none sm:grid-cols-[auto_auto]">
          <div className="min-w-0 px-4 py-3 sm:px-5">
            <dt className="text-xs font-medium text-slate-500">
              Numéro de formulaire
            </dt>
            <dd className="mt-1 break-words font-semibold text-slate-800">
              {formData.numeroFormulaire}
            </dd>
          </div>
          <div className="min-w-0 border-t border-gray-100 px-4 py-3 sm:border-l sm:border-t-0 sm:px-5">
            <dt className="text-xs font-medium text-slate-500">Date</dt>
            <dd className="mt-1 font-medium text-slate-700">
              <time dateTime={formData.dateSoumission}>
                {new Intl.DateTimeFormat("fr-CA", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  timeZone: "UTC",
                }).format(new Date(formData.dateSoumission))}
              </time>
            </dd>
          </div>
        </dl>

        <div className="my-6 flex w-full justify-center">
          <div className="w-fit max-w-full rounded-lg border border-blue-100 border-l-4 border-l-blue-500 bg-blue-50 px-4 py-3 text-blue-800 shadow-sm">
            <p className="flex items-center gap-2 text-lg font-semibold">
              <AcademicCapIcon className="h-5 w-5 flex-none text-blue-600" />
              Demande de participation à une formation
            </p>
          </div>
        </div>

        <SectionHeader icon={UserIcon}>
          Information du membre du personnel
        </SectionHeader>

        <div className={sectionCardClassName}>
          <div className="p-5 sm:p-6">
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-800">
                Renseignements du profil
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Ces informations proviennent de votre compte professionnel.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ReadOnlyInfo label="Nom" value={formData.nom} />
              <ReadOnlyInfo label="Courriel" value={formData.courriel} />
              <ReadOnlyInfo
                label="Lieu de travail"
                value={formData.lieuTravail}
              />
              <ReadOnlyInfo label="Poste" value={formData.poste} />
            </div>
          </div>

          <div className="rounded-b-xl border-t border-gray-200 bg-white p-5 sm:p-6">
            <div className="mb-5">
              <p className="text-sm font-semibold text-gray-800">
                Responsables de l’approbation
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Recherchez et sélectionnez les personnes responsables.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="w-full min-w-0" id="managerInfo">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Supervision immédiate
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <div className="relative [&_input]:text-[15px] [&_input::placeholder]:text-slate-500 [&_input::placeholder]:opacity-100">
                  {!formData.managerInfo.nom && (
                    <UsersList
                      onUserSelect={(selectedUser) =>
                        handleUserSelect(selectedUser, "managerInfo")
                      }
                      initialValue={formData.managerInfo.email}
                    />
                  )}

                  {formData.managerInfo.nom && (
                    <div className="flex min-h-[62px] items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                        {formData.managerInfo.nom.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex min-w-0 flex-col">
                        <span className="break-words text-[15px] font-semibold text-slate-900">
                          {formData.managerInfo.nom}
                        </span>
                        <span className="break-words text-[13px] leading-5 text-slate-600">
                          {formData.managerInfo.email}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((current) => ({
                            ...current,
                            managerInfo: {
                              nom: "",
                              email: "",
                              userId: "",
                              matricule: "",
                            },
                          }))
                        }
                        className="ml-auto flex h-8 w-8 flex-none items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        aria-label="Retirer la supervision immédiate"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                <div id="managerInfo-error">
                  {renderError("managerInfo")}
                </div>
              </div>

              <div
                className="w-full min-w-0"
                id="directionSecteurSurintendance"
              >
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Direction de service / secteur / surintendance
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <div className="relative [&_input]:text-[15px] [&_input::placeholder]:text-slate-500 [&_input::placeholder]:opacity-100">
                  {!formData.directionSecteurSurintendance.nom && (
                    <UsersList
                      onUserSelect={(selectedUser) =>
                        handleUserSelect(
                          selectedUser,
                          "directionSecteurSurintendance",
                        )
                      }
                      initialValue={
                        formData.directionSecteurSurintendance.email
                      }
                    />
                  )}

                  {formData.directionSecteurSurintendance.nom && (
                    <div className="flex min-h-[62px] items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                        {formData.directionSecteurSurintendance.nom
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                      <div className="flex min-w-0 flex-col">
                        <span className="break-words text-[15px] font-semibold text-slate-900">
                          {formData.directionSecteurSurintendance.nom}
                        </span>
                        <span className="break-words text-[13px] leading-5 text-slate-600">
                          {formData.directionSecteurSurintendance.email}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((current) => ({
                            ...current,
                            directionSecteurSurintendance: {
                              nom: "",
                              email: "",
                              userId: "",
                              matricule: "",
                            },
                          }))
                        }
                        className="ml-auto flex h-8 w-8 flex-none items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        aria-label="Retirer la direction sélectionnée"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                <div id="directionSecteurSurintendance-error">
                  {renderError("directionSecteurSurintendance")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <SectionHeader icon={AcademicCapIcon}>
          Description de la sortie
        </SectionHeader>

        <div className={sectionCardClassName}>
          <div className="space-y-5 p-5 sm:p-6">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Renseignements sur la formation
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Précisez les objectifs, le lieu et la durée de la formation.
              </p>
            </div>
            <div>
              <label htmlFor="descriptionObjectifs" className={labelClassName}>
                Courte description et objectifs
                <span className="ml-1 text-red-500"> *</span> :
              </label>
              <textarea
                id="descriptionObjectifs"
                name="descriptionObjectifs"
                value={formData.descriptionObjectifs}
                onChange={handleChange}
                rows={5}
                required
                className={`${inputClassName} resize-y`}
                aria-invalid={Boolean(errors.descriptionObjectifs)}
                aria-describedby={
                  errors.descriptionObjectifs
                    ? "descriptionObjectifs-error"
                    : undefined
                }
              />
              <div id="descriptionObjectifs-error">
                {renderError("descriptionObjectifs")}
              </div>
            </div>

            <div>
              <div className="w-full sm:max-w-sm">
                <p id="modeParticipation-label" className={labelClassName}>
                  Mode de participation
                  <span className="ml-1 text-red-500"> *</span> :
                </p>
                <div
                  className="mt-1.5 flex flex-wrap items-center gap-2"
                  role="radiogroup"
                  aria-labelledby="modeParticipation-label"
                  aria-invalid={Boolean(errors.modeParticipation)}
                  aria-describedby={
                    errors.modeParticipation
                      ? "modeParticipation-error"
                      : undefined
                  }
                >
                  {PARTICIPATION_MODE_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected =
                      formData.modeParticipation === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() =>
                          handleChange({
                            target: {
                              name: "modeParticipation",
                              value: option.value,
                            },
                          })
                        }
                        className={`inline-flex min-h-11 min-w-[9.5rem] items-center justify-center gap-2 rounded-lg border px-3.5 py-2 text-[15px] font-medium shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-1 ${
                          isSelected
                            ? "border-blue-300 bg-blue-100 text-blue-600"
                            : "border-gray-300 bg-white text-gray-500 hover:border-gray-400 hover:text-gray-600"
                        }`}
                      >
                        <Icon
                          className={`h-3.5 w-3.5 ${
                            isSelected ? "text-blue-600" : "text-gray-500"
                          }`}
                          aria-hidden="true"
                        />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                <div id="modeParticipation-error">
                  {renderError("modeParticipation")}
                </div>
              </div>
            </div>

            {formData.modeParticipation === "presentiel" && (
              <>
                <div
                  className={`grid grid-cols-1 gap-5 sm:gap-6 ${
                    formData.pays === "Canada"
                      ? "sm:grid-cols-2 lg:grid-cols-3"
                      : "sm:grid-cols-2"
                  }`}
                >
                  <Field
                    label="Ville"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    required
                    error={errors.ville}
                  />
                  <SelectField
                    label="Pays"
                    name="pays"
                    value={formData.pays}
                    onChange={handleChange}
                    options={COUNTRIES}
                    required
                    error={errors.pays}
                  />
                  {formData.pays === "Canada" && (
                    <SelectField
                      label="Province ou territoire"
                      name="province"
                      value={formData.province}
                      onChange={handleChange}
                      options={CANADIAN_PROVINCES}
                      required
                      error={errors.province}
                    />
                  )}
                </div>

                {(afficherAvertissementAvantSoumission ||
                  afficherAvertissementSuperviseur) && (
                  <div
                    className="flex max-w-5xl items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-950"
                    role="status"
                    aria-live="polite"
                  >
                    <FiAlertTriangle
                      className="mt-1 h-4 w-4 flex-none text-amber-600"
                      aria-hidden="true"
                    />
                    <p>
                      {afficherAvertissementSuperviseur ? (
                        <>
                          Cette demande concerne une formation à l’extérieur
                          {formationHorsCanada
                            ? " du Canada"
                            : " de l’Ontario"}
                          . Si vous l’approuvez, elle sera transmise au
                          programmeur responsable pour l’approbation finale.
                        </>
                      ) : (
                        <>
                          Cette formation se déroule à l’extérieur
                          {formationHorsCanada
                            ? " du Canada"
                            : " de l’Ontario"}
                          . Assurez-vous d’avoir validé votre participation avec
                          votre supervision immédiate avant de soumettre la
                          demande.
                        </>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
              <div className="min-w-0 text-[15px]">
                <p className={labelClassName}>
                  Date de début<span className="ml-1 text-red-500"> *</span>{" "}
                  :
                </p>
                <ModernDatePicker
                  value={formData.dateDebut}
                  onChange={(date) =>
                    handleChange({ target: { name: "dateDebut", value: date } })
                  }
                  ariaLabel="Date de début de la formation (obligatoire)"
                />
                <div id="dateDebut-error">{renderError("dateDebut")}</div>
              </div>
              <div className="min-w-0 text-[15px]">
                <p className={labelClassName}>
                  Date de fin<span className="ml-1 text-red-500"> *</span>{" "}
                  :
                </p>
                <ModernDatePicker
                  value={formData.dateFin}
                  min={formData.dateDebut || undefined}
                  onChange={(date) =>
                    handleChange({ target: { name: "dateFin", value: date } })
                  }
                  ariaLabel="Date de fin de la formation (obligatoire)"
                />
                <div id="dateFin-error">{renderError("dateFin")}</div>
              </div>
              <Field
                label="Nombre de jours"
                name="nombreJours"
                value={formData.nombreJours}
                onChange={handleChange}
                type="number"
                min="1"
                readOnly
                required
                error={errors.nombreJours}
              />
            </div>

            {/* Question temporairement masquée dans l'interface.
            <div>
              <YesNoField
                label="La formation nécessite-t-elle un voyage à l’extérieur de l’Ontario?"
                name="voyageExterieurOntario"
                value={formData.voyageExterieurOntario}
                onChange={handleChange}
                required
                error={errors.voyageExterieurOntario}
              />
            </div>
            */}
          </div>

          <div className="space-y-5 rounded-b-xl border-t border-gray-200 p-5 sm:p-6">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Besoins de suppléance
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Indiquez si un remplacement est nécessaire pendant votre
                absence.
              </p>
            </div>
            <div>
              <YesNoField
                label="Suppléance requise"
                name="suppleanceRequise"
                value={formData.suppleanceRequise}
                onChange={handleChange}
                required
                error={errors.suppleanceRequise}
              />
            </div>

            {formData.suppleanceRequise === "oui" && (
              <>
                <div className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2 sm:gap-6">
                  <Field
                    label="Nombre de jours de suppléance"
                    name="nombreJoursSuppleance"
                    value={formData.nombreJoursSuppleance}
                    onChange={handleChange}
                    type="number"
                    min="1"
                    required
                    error={errors.nombreJoursSuppleance}
                  />
                  <YesNoField
                    label="Suppléance payée de fonds centraux"
                    name="suppleanceFondsCentraux"
                    value={formData.suppleanceFondsCentraux}
                    onChange={handleChange}
                    required
                    error={errors.suppleanceFondsCentraux}
                  />
                </div>

                {formData.suppleanceFondsCentraux === "oui" && (
                  <div>
                    <label
                      htmlFor="justificationFondsCentraux"
                      className={labelClassName}
                    >
                      Justification pour une suppléance payée de fonds centraux
                      <span className="ml-1 text-red-500"> *</span> :
                    </label>
                    <textarea
                      id="justificationFondsCentraux"
                      name="justificationFondsCentraux"
                      value={formData.justificationFondsCentraux}
                      onChange={handleChange}
                      rows={4}
                      required
                      className={`${inputClassName} resize-y`}
                      aria-invalid={Boolean(errors.justificationFondsCentraux)}
                      aria-describedby={
                        errors.justificationFondsCentraux
                          ? "justificationFondsCentraux-error"
                          : undefined
                      }
                    />
                    <div id="justificationFondsCentraux-error">
                      {renderError("justificationFondsCentraux")}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <SectionHeader icon={CurrencyDollarIcon} iconColor="text-yellow-400">
          Estimation des dépenses
        </SectionHeader>

        <div className="mt-5 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="px-4 py-4 sm:px-5">
              <h3 className="text-[15px] font-semibold text-slate-900">
                Dépenses prévues
              </h3>
              <p className="mt-1 text-[13px] text-slate-500">
                Ajoutez uniquement les catégories applicables à cette demande.
              </p>
            </div>

            {costRows.length === 0 && (
              <div className="border-t border-slate-200 px-4 py-7 text-center sm:px-5">
                <p className="text-sm font-medium text-slate-600">
                  Aucune dépense ajoutée.
                </p>
              </div>
            )}

            {costRows.length > 0 && (
            <div className="divide-y divide-slate-200 border-t border-slate-200">
              {costRows.map((row, index) => {
                const isDuplicateType =
                  row.type &&
                  costRows.filter(({ type }) => type === row.type).length > 1;

                return (
                  <div
                    key={row.id}
                    className="grid grid-cols-1 items-start gap-x-5 gap-y-3 px-4 py-5 sm:px-5 lg:grid-cols-[minmax(0,320px)_minmax(0,200px)_minmax(0,360px)_40px]"
                  >
                    <div className="min-w-0">
                      <label
                        htmlFor={`costType-${row.id}`}
                        className="block text-sm font-medium leading-5 text-slate-700"
                      >
                        Type de dépense <span className="text-red-700">*</span> :
                      </label>
                      <select
                        id={`costType-${row.id}`}
                        value={row.type}
                        onChange={(event) =>
                          handleCostTypeChange(row.id, event.target.value)
                        }
                        required
                        className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[15px] text-slate-800 shadow-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      >
                            <option value="">Sélectionner une dépense</option>
                        {COST_OPTIONS.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {renderError(`costType-${row.id}`)}
                    </div>

                    <div className="min-w-0 lg:col-start-2 lg:row-start-1">
                      {row.type && (
                        <MoneyField
                          label="Montant"
                          name={`costAmount-${row.id}`}
                          value={row.montant}
                          onChange={(event) =>
                            handleCostRowChange(
                              row.id,
                              "montant",
                              event.target.value,
                            )
                          }
                          required
                          error={errors[`costAmount-${row.id}`]}
                        />
                      )}
                      {!row.type && (
                        <span className="hidden h-10 items-center text-slate-400 lg:flex">
                          —
                        </span>
                      )}
                    </div>

                    <div className="min-w-0 lg:col-start-3 lg:row-start-1">
                      {row.type && (
                        <>
                          <label
                            htmlFor={`details-${row.id}`}
                            className="block text-sm font-medium leading-5 text-slate-700"
                          >
                            Détails / commentaires
                            {isDuplicateType && (
                              <span className="text-red-700"> *</span>
                            )}{" "}
                            :
                          </label>
                          <textarea
                            id={`details-${row.id}`}
                            name={`costDetails-${row.id}`}
                            value={row.details}
                            onChange={(event) =>
                              handleCostRowChange(
                                row.id,
                                "details",
                                event.target.value,
                              )
                            }
                            rows={2}
                            maxLength={250}
                            required={Boolean(isDuplicateType)}
                            aria-invalid={Boolean(
                              errors[`costDetails-${row.id}`]
                            )}
                            className={`mt-1.5 min-h-16 w-full resize-y rounded-lg border bg-white px-3 py-2 text-[15px] text-slate-800 shadow-sm outline-none transition-colors focus:ring-2 ${
                              errors[`costDetails-${row.id}`]
                                ? "border-red-400 focus:border-red-500 focus:ring-red-100"
                                : "border-slate-300 focus:border-blue-500 focus:ring-blue-100"
                            }`}
                          />
                          {renderError(`costDetails-${row.id}`)}
                        </>
                      )}
                    </div>

                    <div className="flex min-h-10 items-center lg:col-start-4 lg:row-start-1 lg:mt-[26px] lg:justify-center">
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => removeCostRow(row.id)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-transparent text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                          aria-label={`Supprimer la dépense ${index + 1}`}
                          title="Supprimer cette dépense"
                        >
                          <FiTrash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            {!isReadOnly && (
              <div className="border-t border-slate-200 p-3">
                <button
                  type="button"
                  onClick={addCostRow}
                  disabled={
                    costRows.some(({ type }) => !type)
                  }
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-blue-200 bg-blue-50/60 px-4 py-2 text-sm font-semibold text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FiPlus className="h-4 w-4" aria-hidden="true" />
                  Ajouter une dépense
                </button>
              </div>
            )}

            <div
              id="totalEstime"
              className="border-t border-slate-200 bg-slate-50 px-4 py-4 sm:px-5"
            >
              <div className="grid grid-cols-[1fr_auto] items-start gap-x-5 gap-y-1 lg:grid-cols-[minmax(0,320px)_minmax(0,200px)_minmax(0,360px)_40px]">
                <p className="text-sm font-semibold text-slate-700">
                  Total estimé
                </p>
                <div className="text-right lg:col-start-2">
                  <p
                    className={`text-xl font-bold ${
                      errors.totalEstime ? "text-red-600" : "text-slate-900"
                    }`}
                  >
                    {formattedTotal}
                  </p>
                  <div id="totalEstime-error">
                    {renderError("totalEstime")}
                  </div>
                </div>
              </div>
            </div>
        </div>

        <SectionHeader icon={PencilAltIcon} iconColor="text-green-400">
          Signature du demandeur
        </SectionHeader>

        <div
          className={`${sectionCardClassName} p-5 sm:p-6`}
          id="signatureElectronique"
        >
          <label className="relative flex max-w-5xl cursor-pointer items-start gap-4 sm:items-center">
            <input
              type="checkbox"
              name="signatureElectronique"
              checked={formData.signatureElectronique === "oui"}
              onChange={handleCertificationChange}
              className="absolute opacity-0 w-6 h-6"
              required
            />
            <span
              className={`flex h-6 w-6 flex-none items-center justify-center rounded-md border-2 transition-all duration-200 ${
                formData.signatureElectronique === "oui"
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-300 bg-slate-50"
              }`}
            >
              {formData.signatureElectronique === "oui" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </span>
            <span className="select-none text-sm font-medium leading-6 text-slate-900">
              Je certifie que les informations fournies sont exactes, complètes
              et vérifiées.
            </span>
          </label>
          <div id="signatureElectronique-error" className="max-w-5xl">
            {renderError("signatureElectronique")}
          </div>
        </div>
        </fieldset>

        {canProcess && (
          <div className="mt-8 w-full max-w-5xl rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <label
              htmlFor="commentaireParticipationFormation"
              className="block text-sm font-medium text-slate-700"
            >
              Commentaire :
            </label>
            <textarea
              id="commentaireParticipationFormation"
              name="commentaire"
              value={commentaire}
              onChange={(event) => setCommentaire(event.target.value)}
              rows={4}
              placeholder="Ajoutez un commentaire..."
              className={`${inputClassName} resize-y`}
            />
          </div>
        )}

        {selectedForm && (
          <section
            aria-labelledby="historique-commentaires-formation"
            className="mt-8 w-full max-w-5xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          >
            <div className="border-b border-slate-100 px-4 py-4 sm:px-5">
              <h3
                id="historique-commentaires-formation"
                className="text-base font-semibold text-slate-900"
              >
                Historique du traitement
                <span className="ml-2 font-normal text-slate-500">
                  ({historiqueTraitement.length})
                </span>
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Consultez les personnes, les états et les commentaires associés
                au traitement de cette demande.
              </p>
            </div>

            <div className="p-4 sm:p-5">
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full min-w-[640px] table-fixed divide-y divide-slate-200 bg-white">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="w-1/4 p-3 text-left text-sm font-semibold text-slate-700">
                        Auteur
                      </th>
                      <th className="w-2/5 p-3 text-left text-sm font-semibold text-slate-700">
                        Commentaire
                      </th>
                      <th className="w-1/5 p-3 text-left text-sm font-semibold text-slate-700">
                        Date et heure
                      </th>
                      <th className="p-3 text-left text-sm font-semibold text-slate-700">
                        État
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {historiqueTraitement.map((traitant, index) => (
                      <tr
                        key={`${traitant.userId || "traitant"}-${traitant.dateHeure || index}`}
                        className="transition-colors hover:bg-slate-50"
                      >
                        <td className="break-words p-3 align-top">
                          <span className="block text-[15px] font-medium text-slate-800">
                            {traitant.nom || "—"}
                          </span>
                          {traitant.email && (
                            <span className="mt-1 block break-all text-xs text-slate-500">
                              {traitant.email}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-pre-wrap break-words p-3 align-top text-[15px] leading-6 text-slate-700">
                          {traitant.commentaire?.trim() || "Aucun commentaire"}
                        </td>
                        <td className="break-words p-3 align-top text-sm text-slate-500">
                          {traitant.dateHeure
                            ? new Intl.DateTimeFormat("fr-CA", {
                                dateStyle: "medium",
                                timeStyle: "short",
                              }).format(new Date(traitant.dateHeure))
                            : "—"}
                        </td>
                        <td className="break-words p-3 align-top text-sm font-medium text-slate-700">
                          {traitant.etatDemande || "—"}
                        </td>
                      </tr>
                    ))}
                    {historiqueTraitement.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-8 text-center text-sm text-slate-500"
                        >
                          Aucun traitement enregistré pour cette demande.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        <div className="mt-12 flex justify-start pb-4">
          <div className="flex flex-wrap gap-3">
            {page !== "Mes taches" &&
              ["", "Brouillon"].includes(formData.etatDemande) && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSubmitting}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:border-slate-600 hover:bg-slate-600 active:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiSave className="h-4 w-4 flex-none" aria-hidden="true" />
                  Enregistrer
                </button>
              )}
            {page !== "Mes taches" &&
              ["", "Brouillon", "Rejeté"].includes(
                formData.etatDemande,
              ) && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-green-700 bg-green-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:border-green-800 hover:bg-green-800 active:bg-green-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiSend className="h-4 w-4 flex-none" aria-hidden="true" />
                  Soumettre
                </button>
              )}
            {canProcess && (
              <>
                <button
                  type="button"
                  onClick={handleRejeter}
                  disabled={isSubmitting}
                  className="inline-flex min-h-10 min-w-24 items-center justify-center rounded-lg border border-red-700 bg-red-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Rejeter
                </button>
                <button
                  type="button"
                  onClick={handleApprouve}
                  disabled={isSubmitting}
                  className="inline-flex min-h-10 min-w-24 items-center justify-center rounded-lg border border-green-700 bg-green-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Approuver
                </button>
              </>
            )}
          </div>
        </div>
      </form>

      {isSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div
            className={`flex transform items-center space-x-3 rounded-lg bg-white px-5 py-4 ${couleur} shadow-xl transition-all duration-500 ease-in-out animate-bounce`}
            role="status"
            aria-live="polite"
          >
            <CheckCircleIcon
              className={`h-8 w-8 ${couleur} animate-pulse`}
              aria-hidden="true"
            />
            <span className="max-w-xl text-lg font-semibold">{message}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParticipationFormation;
